import { Form, Formio } from '@formio/react';
import { Box, CircularProgress, styled } from '@mui/material';
import { useEffect, useMemo, useReducer, useRef, useState } from 'react';

import { OnGetMetadataSignature, OnSetMetadataSignature } from './MetadataEventTypes';
import { FormProps } from '@formio/react/lib/components/Form';
import { transformToDBValues, transformToFormIoValues } from 'utils/formio-utils';
import { GuidOrNumber } from 'models/common';
import FormioCustomComponents from 'components/formio';
// Register all custom components with Form.io
Formio.use(FormioCustomComponents);

const ContainerBox = styled(Box)({
    height: '100%',
    '& .col-form-label': {
        fontWeight: 'bold',
        fontFamily: 'Open Sans',
        fontSize: '14px'
    },
    '& .invalid-feedback': {
        display: 'flex',
        justifyContent: 'flex-end'
    },
    '& .card-body:has(.formio-editor-read-only-content)': {
        backgroundColor: '#e9ecef !important',
        opacity: 0.9,
        padding: '.375rem .75rem',
        whiteSpace: 'normal',
    },
    '& .formio-editor-read-only-content': {
        minHeight: '1.5rem'
    }
});

type MetadataFormProps = {
    loading?: boolean;
    metadataId: GuidOrNumber;
    components: any[];
    onLoadValues: OnGetMetadataSignature;
    onUpdateValue: OnSetMetadataSignature;
};

const MetadataForm: React.FC<MetadataFormProps> = (props) => {
    const {
        metadataId,
        onLoadValues,
        onUpdateValue
    } = props;

    const isEqual = require('lodash.isequal');
    const [refresh, forceRefresh] = useReducer(v => v + 1, 0);

    const [values, setValues] = useState<any>({});
    const [localLoading, setLocalLoading] = useState(false);

    const loading = useMemo(() => {
        return props.loading || localLoading;
    }, [props.loading, localLoading]);

    useEffect(() => {
        setLocalLoading(false);
    }, [values]);

    const components = useMemo(() => {
        if (props.components.length === 0) return [];
    
        let newComponents = [];
        for (let component of props.components) {
            const componentCopy = {...component};
            
            if (componentCopy.dataSrc === "url") {
                componentCopy.limit = 20;
            }
            
            if (componentCopy.properties?.mandatory) {
                let baseLabel = componentCopy.label as string;
                
                const taggedReqPattern = /<[^>]*>\s*\(required\)\s*<\/[^>]*>/g;
                baseLabel = baseLabel.replace(taggedReqPattern, '');
                
                const plainReqPattern = /\s*\(required\)\s*/g;
                baseLabel = baseLabel.replace(plainReqPattern, '');
                
                componentCopy.errorLabel = baseLabel.trim();
                
                const hasValue = values && 
                 componentCopy.key && 
                 values[componentCopy.key] !== undefined && 
                 values[componentCopy.key] !== null && 
                 values[componentCopy.key] !== '' && 
                 !(Array.isArray(values[componentCopy.key]) && values[componentCopy.key].length === 0);
                
                const color = hasValue ? 'black' : '#dc3545';            
                                
                componentCopy.label = `${componentCopy.errorLabel} <em style="color: ${color}">(required)</em>`;
            }

            if(componentCopy.customClass === 'sensitive') {
                componentCopy.label = `<span class="chevron-icon"></span>${componentCopy.label}`;
            }
    
            newComponents.push(componentCopy);
        }
    
        return newComponents;
    }, [props.components, values]);

    const loadMetadata = async () => {
        setLocalLoading(true);
        const newValues = await onLoadValues(metadataId);
        transformToFormIoValues(newValues);
        setValues(newValues);
        setLocalLoading(false);
    };

    useEffect(() => {
        if (metadataId === 0) return;
        loadMetadata();
    }, [metadataId]);

    useEffect(() => {
        if (localLoading) {
            setLocalLoading(false);
            return;
        }
        setLocalLoading(true);
        forceRefresh();
    }, [refresh]);

    const fireUpdateValues = async (id: GuidOrNumber, originalValue: any, newValue: any, isValid: boolean) => {
        if (isEqual(values, newValue))
            return;
        for (const key in values) {
            if (!(key in newValue)) {
                newValue[key] = "";
            }
        }
        const newValues = await onUpdateValue(id, originalValue, newValue, isValid);
        transformToFormIoValues(newValues);
        setValues(newValues);
    };

    const handleBlur = (component: any) => {
        if (!component) return;
        switch (component.type) {
            case 'datetime':
            case 'radio':
            case 'checkbox':
            case 'select':
                return;
            default:
                if (component.pristine)
                    return;
                break;
        }
        component.triggerChange();


    };

    const handleChange = (submission: any) => {
        if (!submission || !submission.changed || !submission.changed.component) return;
        switch (submission.changed.component.type) {
            case 'datetime':
            case 'radio':
            case 'checkbox':
            case 'select':
                break;
            default:
                if (submission.changed.instance.element.contains(document.activeElement))
                    return;
                break;
        }
        if (submission.changed.instance.pristine)
            return;
        if (submission.changed.instance?.data
            && submission.isValid
            && !isEqual(values, submission?.changed?.instance?.data)) {
            transformToDBValues(submission.changed.instance.data);
            fireUpdateValues(metadataId, values, submission?.changed?.instance?.data, submission.isValid);
        }
    };

    const formProps = useMemo<FormProps>(() => ({
        submission: {
            data: values
        },
        onBlur: handleBlur,
        onChange: handleChange
    }), [values])

    if (loading) {
        return <CircularProgress />
    }

    return (
        <ContainerBox id="parent-metadata-container">
            <Form
                form={{
                    components: components
                }}
                {...formProps}
            />
        </ContainerBox>
    )
};

export default MetadataForm;